import { browser } from "$app/environment";
import type { CategoryPreviewData, FeatureMapPoint, KeepExploringCategory } from "$lib/api/velatlas/response.types";
import { CITY_FEATURE_KEEP_EXPLORING, CITY_FEATURE_TYPE_LABELS, type CityFeatureType } from "$lib/features/cityFeature/cityFeatureType.enum";
import type { SimplePlace } from "$lib/features/place/place.type";
import { firstThatFits, META_DESCRIPTION_MAX, META_TITLE_MAX } from "$lib/utils/meta.util";
import { UrlUtil } from "$lib/utils/url.util";

export function listFormatByAnd(names: string[], customStyle: 'onlyCommas' | 'endInAnd' = 'endInAnd'): string {
    const style = customStyle === 'onlyCommas' ? 'narrow' : 'short';
    return new Intl.ListFormat('en-GB', { style: style, type: 'conjunction' }).format(names);
}

export function buildMetaTitleForStandalonePage({
    itemEnglishName,
    cityName,
    isUnique,
    place
}: {
    itemEnglishName: string;
    cityName: string;
    isUnique: boolean;
    place?: SimplePlace | null;
}): string {
    const hasCity = nameIncludesCity(itemEnglishName, cityName);

    // 1. Prevent duplicate city if it's already in the name
    // e.g. "Miami Seaquarium" -> "Miami Seaquarium" (NOT "Miami Seaquarium, Miami")
    const locationName = hasCity ? itemEnglishName : `${itemEnglishName}, ${cityName}`;

    const candidates: string[] = [];

    // 2. High-CTR Candidates for UNESCO Sites
    if (place?.unescoId) {
        candidates.push(
            `${locationName}: UNESCO Site Guide & Map`,
            `${locationName} - UNESCO World Heritage Site`,
            ...(isUnique && !hasCity ? [`${itemEnglishName}: UNESCO Site Guide & Map`] : [])
        );
    }

    // 3. High-CTR Candidates for Regular Attractions (Tiered by Length)
    // - Uses ":" instead of " - " to save characters and look editorial.
    // - "Visitor Guide" performs significantly better than "Guide" for search intent.
    candidates.push(
        `${locationName}: Visitor Guide, History & Map`,
        `${locationName}: Visitor Guide & Map`,
        `${locationName} - Guide, History & Map`
    );

    // 4. Fallbacks dropping the city when globally unique (saves room for long attraction names)
    if (isUnique && !hasCity) {
        candidates.push(
            `${itemEnglishName}: Visitor Guide, History & Map`,
            `${itemEnglishName}: Visitor Guide & Map`,
            `${itemEnglishName} - Guide & Map`
        );
    }

    // 5. Compact fallbacks to prevent truncation on long names
    candidates.push(
        `${locationName} - Guide & Map`,
        locationName,
        itemEnglishName
    );

    return firstThatFits(candidates, META_TITLE_MAX);
}

export function buildMetaDescriptionForStandalonePage({
    itemEnglishName,
    cityName,
    isUnique = false,
    place,
    factsLength,
    faqsLength,
    videosLength,
    durationHours
}: {
    itemEnglishName: string;
    cityName: string;
    isUnique?: boolean;
    place?: SimplePlace | null;
    factsLength: number;
    faqsLength: number;
    videosLength: number;
    durationHours?: number;
}): string {
    const listFormatter = new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' });
    const hasCity = nameIncludesCity(itemEnglishName, cityName);

    const subject = isUnique || hasCity ? itemEnglishName : itemEnglishName;
    const subjectVariants = isUnique || hasCity 
        ? [subject] 
        : [subject, `${itemEnglishName} in ${cityName}`];

    const totalFacts = factsLength + faqsLength;
    const factsItem = `${totalFacts} facts & FAQs`;
    const videoItem = videosLength > 0 ? 'a video tour' : null;
    const mapItem = 'an interactive map';

    const fullFeatures = [mapItem, videoItem, factsItem].filter((i): i is string => !!i);
    const compactFeatures = [videoItem, factsItem].filter((i): i is string => !!i);

    const hookVariants = buildHookCandidates(place);
    const candidates: string[] = [];

    for (const subj of subjectVariants) {
        for (const hook of hookVariants) {
            const s1 = `Explore ${subj}, ${hook}.`;

            if (durationHours && fullFeatures.length > 0) {
                candidates.push(`${s1} Plan a ${durationHours}h visit with ${listFormatter.format(fullFeatures)}.`);
            }
            if (fullFeatures.length > 0) {
                candidates.push(`${s1} Plan your visit with ${listFormatter.format(fullFeatures)}.`);
            }
            if (compactFeatures.length > 0) {
                candidates.push(`${s1} Plan a ${durationHours}h visit with ${listFormatter.format(compactFeatures)}.`);
            }
        }

        const richIntros = [
            `Discover ${subj}: history, highlights & visitor tips.`,
            `Explore ${subj}: history, key events & visitor tips.`,
            `Plan your visit to ${subj} with full history & visitor tips.`,
            `Plan your visit to ${subj}.`
        ];

        for (const intro of richIntros) {
            if (durationHours && fullFeatures.length > 0) {
                candidates.push(`${intro} Plan a ${durationHours}h visit with ${listFormatter.format(fullFeatures)}.`);
                candidates.push(`${intro} Explore in ${durationHours}h with ${listFormatter.format(fullFeatures)}.`);
            }
            if (fullFeatures.length > 0) {
                candidates.push(`${intro} Includes ${listFormatter.format(fullFeatures)}.`);
            }
            if (compactFeatures.length > 0) {
                candidates.push(`${intro} Explore in ${durationHours}h with ${listFormatter.format(compactFeatures)}.`);
            }
        }

        // --- Safety Fallback ---
        candidates.push(
            `Plan your visit to ${subj} with an interactive map, video tour, and FAQs.`,
            `Complete visitor guide to ${subj}, featuring an interactive map, history, and FAQs.`
        );
    }

    return firstThatFits(candidates, META_DESCRIPTION_MAX);
}


/**
 * Returns the first available image from the given items, preferring the primary image if available.
 * @param items which either have a a feature and/or a primary field
 * @returns the first found image, or undefined if none are found
 */
export function findFirstAvailableImage<T extends { images: TImage[] }, TImage>(items: { feature: T; primary?: { images: TImage[] } | null }[]): TImage | undefined {
    for (const item of items) {
        if (item.primary && item.primary.images.length > 0) return item.primary.images[0];
        if (item.feature.images.length > 0) return item.feature.images[0];
    }
    return undefined;
}

/**
 * Filters all available city categories against the preferred fallback map, ensuring exactly 2 highly relevant "Keep Exploring" cards are returned.
 */
export function buildKeepExploringCategories(
    currentFeatureType: CityFeatureType,
    allCategoriesData: CategoryPreviewData[],
    countrySlug: string,
    citySlug: string
): KeepExploringCategory[] {
    const maxCategories: number = 4;
    const preferredTypes = CITY_FEATURE_KEEP_EXPLORING[currentFeatureType];
    const result: KeepExploringCategory[] = [];

    const mapToClientInterface = (data: CategoryPreviewData): KeepExploringCategory => ({
        type: data.type,
        title: CITY_FEATURE_TYPE_LABELS[data.type].toUpperCase(),
        image: data.representativeImage,
        count: data.count,
        href: UrlUtil.toCityFeatures({ countrySlug, citySlug, type: data.type }).href
    });

    for (const targetType of preferredTypes) {
        if (result.length >= maxCategories) break;
        const match = allCategoriesData.find(
            (d) => d.type === targetType && d.count > 0 && d.representativeImage !== null
        );
        if (match) {
            result.push(mapToClientInterface(match));
        }
    }

    // SAFETY NET: If the city is very small and didn't have the preferred types, find ANY other available categories to ensure the section isn't empty.
    if (result.length < maxCategories) {
        for (const data of allCategoriesData) {
            if (result.length >= maxCategories) break;

            const hasData = data.count > 0;
            const isNotCurrentFeature = data.type !== currentFeatureType;
            const isNotAlreadyAdded = !result.some((r) => r.type === data.type);
            if (hasData && isNotCurrentFeature && isNotAlreadyAdded) {
                result.push(mapToClientInterface(data));
            }
        }
    }

    return result;
}

export function metaTitleItemsCounter(items: Array<object>, { minItems = 7 }: { minItems?: number } = {}): string {
    return items.length >= minItems ? `${items.length} ` : '';
}

export function getThemeColor(variableName: string, { fallback }: { fallback: string } = { fallback: '#FFFFFF' }): string {
    if (browser && typeof window !== 'undefined') {
        const value = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
        return value || fallback;
    }
    return fallback;
}

export function getTopFrequentValues<T extends string>(valueLists: T[][], count = 2): T[] {
    const frequency = new Map<T, number>();
    for (const values of valueLists) {
        for (const value of values) {
            frequency.set(value, (frequency.get(value) ?? 0) + 1);
        }
    }
    return [...frequency.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, count)
        .map(([value]) => value);
}

export function generateUUIDv7(): string {
    const bytes = new Uint8Array(16);

    // 1. Get the current Unix timestamp in milliseconds
    const timestamp = Date.now();

    // 2. Write the 48-bit timestamp into the first 6 bytes (Big Endian)
    bytes[0] = (timestamp / 0x10000000000) & 0xff;
    bytes[1] = (timestamp / 0x100000000) & 0xff;
    bytes[2] = (timestamp / 0x1000000) & 0xff;
    bytes[3] = (timestamp / 0x10000) & 0xff;
    bytes[4] = (timestamp / 0x100) & 0xff;
    bytes[5] = timestamp & 0xff;

    // 3. Fill the remaining 10 bytes with cryptographically strong random values
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes.subarray(6));
    } else {
        // Fallback for environments where crypto is not available
        for (let i = 6; i < 16; i++) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }

    // 4. Set the version to 7 (0111) in the most significant 4 bits of byte 6
    bytes[6] = (bytes[6] & 0x0f) | 0x70;

    // 5. Set the variant to 2 (10xx) in the most significant 2 bits of byte 8
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    // 6. Convert the byte array to the canonical 8-4-4-4-12 hex string format
    const hex = Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function capitalizeWords(input: string): string {
    if (input.length === 0) return input;
    if (input.length === 1) return input.toUpperCase();
    return input
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

export function startsWithVowel(word: string): boolean {
    return /^[aeiou]/i.test(word)
};

export function typedKeys<T extends object>(obj: T): (keyof T)[] {
    return Object.keys(obj) as (keyof T)[];
}

export function sortShortestToLongest(strings: string[]): string[] {
    return [...strings].sort((a, b) => a.length - b.length);
}

export function sortByFrequency(strings: string[]): string[] {
    const counts = new Map<string, number>();
    for (const str of strings) {
        const normalized = str.toLowerCase();
        counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
    }
    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([str]) => str);
}

export function buildExampleItemsPhrase({ names, totalItems, limit, includingWording = 'including' }: { names: string[]; totalItems: number, limit: number, includingWording?: string }): string {
    const hasMoreItemsOtherThanFeatured: boolean = totalItems > limit;
    const featuredItemsPhrase: string = listFormatByAnd(names.slice(0, limit), hasMoreItemsOtherThanFeatured ? 'onlyCommas' : 'endInAnd');
    const hasMoreItemsPhrase: string = hasMoreItemsOtherThanFeatured ? ' & more' : '';
    return `${includingWording} ${featuredItemsPhrase}${hasMoreItemsPhrase}`;
}

// slices it to the nearest word, and ensures it ends with a letter, not full stop or comma etc
export function truncateDescription(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
        return text;
    }
    return text
        .slice(0, maxLength)
        .trim()
        .replace(/\s+\S*$/, '')
        .replace(/[^\p{L}\p{N}]+$/u, '');
}

// finds the string that is closest to the given length, and returns it
export function closestByLength(strings: string[], aimingFor: number = 170): string {
    if (strings.length === 0) {
        throw new Error("The strings array must not be empty.");
    }

    return strings.reduce((closest, current) => {
        const closestDifference = Math.abs(closest.length - aimingFor);
        const currentDifference = Math.abs(current.length - aimingFor);
        return currentDifference < closestDifference ? current : closest;
    });
}

export function calculatePagesNeeded({ totalItems, itemsPerPage }: { totalItems: number, itemsPerPage: number }): number {
    return Math.ceil(totalItems / itemsPerPage);
}

export function paginate<T>({
    items,
    page,
    itemsPerPage,
}: {
    items: T[]
    page: number
    itemsPerPage: number
}): T[] {
    const start = (page - 1) * itemsPerPage
    return items.slice(start, start + itemsPerPage)
}

// ensures that the page is within the bounds of the total pages
export function constructSafePage({ page, totalPages }: { page: number, totalPages: number }): number {
    return Math.min(Math.max(1, page), Math.max(1, totalPages));
}

export function filterOutMapPoints(mapPoints: FeatureMapPoint[], types: CityFeatureType[]): FeatureMapPoint[] {
    return mapPoints.filter((p) => !types.includes(p.type));
}

function nameIncludesCity(itemName: string, cityName: string): boolean {
    if (!cityName?.trim() || !itemName?.trim()) return false;
    const normalize = (s: string) =>
        s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

    const normalizedItem = normalize(itemName);
    const normalizedCity = normalize(cityName.trim());
    const escaped = normalizedCity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i');

    return regex.test(normalizedItem);
}

function buildHookCandidates(place?: SimplePlace | null): string[] {
    if (!place) return [];

    const isUnesco = !!place.unescoId;
    const year = place.inceptionYear
        ? place.inceptionYear < 0
            ? `${Math.abs(place.inceptionYear)} BC`
            : `${place.inceptionYear}`
        : null;

    const exhibits =
        place.exhibitionSize && place.exhibitionSize >= 50
            ? `${formatCountCompact(place.exhibitionSize)} exhibits`
            : null;

    const elevation =
        place.elevationMeters && place.elevationMeters >= 500
            ? `${new Intl.NumberFormat('en-GB').format(Math.round(place.elevationMeters))}m elevation`
            : null;

    const visitors =
        place.visitorsPerYear && place.visitorsPerYear >= 50_000
            ? `${formatCountCompact(place.visitorsPerYear)} annual visitors`
            : null;

    const rawStyle = place.architecturalStyles?.[0];
    const style = rawStyle
        ? rawStyle.toLowerCase().includes('architecture')
            ? `known for its ${rawStyle}`
            : `known for its ${rawStyle} architecture`
        : null;

    // 1. Multi-attribute combos
    const multiModifier: string | null =
        exhibits && visitors
            ? `housing ${exhibits} with ${visitors}`
            : elevation && visitors
                ? `perched at ${elevation} with ${visitors}`
                : null;

    // 2. Single modifiers (order of priority)
    const singleModifiers: string[] = [];
    if (exhibits) singleModifiers.push(`housing ${exhibits}`);
    if (elevation) singleModifiers.push(`perched at ${elevation}`);
    if (visitors) singleModifiers.push(`with ${visitors}`);
    if (style) singleModifiers.push(style);

    const candidates: string[] = [];

    // --- Tier 1: UNESCO + Year + Multi-modifier ---
    if (isUnesco && year && multiModifier) {
        candidates.push(`a UNESCO World Heritage Site from ${year}, ${multiModifier}`);
    }

    // --- Tier 2: Year + Multi-modifier ---
    if (year && multiModifier) {
        candidates.push(`dating back to ${year}, ${multiModifier}`);
    }

    // --- Tier 3: Multi-modifier Alone (NO YEAR) ---
    if (multiModifier) {
        candidates.push(multiModifier);
    }

    // --- Tier 4: Year + Single Modifier ---
    if (year) {
        for (const mod of singleModifiers) {
            candidates.push(`dating back to ${year}, ${mod}`);
        }
    }

    // --- Tier 5: UNESCO Combos ---
    if (isUnesco && year) {
        candidates.push(`a UNESCO World Heritage Site dating back to ${year}`);
    }
    if (isUnesco) {
        for (const mod of singleModifiers) {
            candidates.push(`a UNESCO World Heritage Site ${mod}`);
        }
    }

    // --- Tier 6: Single Hooks ---
    for (const mod of singleModifiers) {
        candidates.push(mod);
    }
    if (year) {
        candidates.push(`dating back to ${year}`);
    }
    if (isUnesco) {
        candidates.push('a UNESCO World Heritage Site');
    }

    return candidates;
}

function formatCountCompact(num: number): string {
    if (num >= 1_000_000) {
        const millions = num / 1_000_000;
        const formatted = millions >= 10 ? Math.floor(millions) : millions.toFixed(1).replace(/\.0$/, '');
        return `${formatted}M+`;
    }
    if (num >= 10_000) {
        return `${Math.floor(num / 1_000)}k+`;
    }
    // For collections between 100 and 9,999, exact rounded numbers look great
    return `${new Intl.NumberFormat('en-GB').format(Math.floor(num / 10) * 10)}+`;
}